import {
  ArrowLeft,
  BookOpen,
  Check,
  ChevronDown,
  ChevronRight,
  Edit2,
  Lock,
  Mail,
  MoreVertical,
  Trash2,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { CarrierPocketbook } from '@/components/carrier-directory/CarrierPocketbook';
import { EvidenceCard } from '@/components/citations';
import { CorrespondenceHistoryCard } from '@/components/correspondence';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ContactHandlesList } from '@/components/ui/ContactHandlesList';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { useIsV3 } from '@/hooks/useAppVersion';
import { useGetCarrierQuery } from '@/services/api/carriersApiService';
import { usePropertiesWithOwnersQuery } from '@/services/api/subjectsService';
import { useCorrespondencePackageGetOrCreateForCoverageMutation } from '@/services/api/correspondenceService';
import {
  useDeleteCoverageMutation,
  useUpdateCoverageMutation,
} from '@/services/api/coverageService';
import {
  CoverageCategory,
  type CoverageListItem,
  CoverageSchema,
  CoverageSource,
  CoverageStatus,
} from '@/services/types/coverage-types';

import { CoverageStatusBadge } from '../CoverageStatusBadge';
import { CoverageSharingBanner } from '../shared/CoverageSharingBanner';
import {
  benefitTypeLabels,
  categoryLabels,
  identifierTypeLabels,
  policyTypeLabelsDetailed,
} from '../shared/constants';
import { DataSourceBadge } from '../shared/DataSourceBadge';
import { formatLimitValue, getPrimaryPolicyIdentifier } from '../shared/utils';
import { isCachedSuggestion } from '../shared/utils/cacheUtils';
import { AutoGeneratedDocuments } from './AutoGeneratedDocuments';

interface CoverageDetailsProps {
  matterId: string;
  coverage: CoverageListItem;
  onBack: () => void;
  onUpdate: () => void;
  backButtonText?: string;
}

const CoverageOverview = ({
  coverage,
  displayCarrierName,
  isPremises,
}: {
  coverage: CoverageSchema;
  displayCarrierName: string;
  isPremises?: boolean;
}) => {
  const primaryPolicyId = getPrimaryPolicyIdentifier(coverage);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      {/* Main coverage identity - like a declaration page header */}
      <div className="flex items-start justify-between mb-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-xl font-semibold text-gray-900">
              {displayCarrierName}
            </h3>
            {coverage.category && (
              <span className="text-sm bg-blue-50 text-blue-700 px-2 py-1 rounded font-medium">
                {categoryLabels[coverage.category]}
              </span>
            )}
          </div>

          <div className="flex items-center gap-4 text-sm text-gray-600">
            {coverage.policy_type && (
              <span className="font-medium text-gray-800">
                {policyTypeLabelsDetailed[coverage.policy_type]}
              </span>
            )}
            {primaryPolicyId && (
              <span className="font-mono text-gray-700">{primaryPolicyId}</span>
            )}
            {isCachedSuggestion(coverage) && (
              <span className="text-green-600 font-medium">
                • Contact info previously verified
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Named insured - displayed prominently without label */}
      {coverage.named_insured && coverage.named_insured.length > 0 && (
        <div className="mb-4">
          <div className="text-sm text-gray-500 mb-1">Insured</div>
          <div className="font-medium text-gray-900">
            {coverage.named_insured.join(', ')}
          </div>
        </div>
      )}

      {/* For premises: show carrier name separately if available */}
      {isPremises && coverage.carrier?.name && (
        <div className="mb-4">
          <div className="text-sm text-gray-500 mb-1">Insurance Carrier</div>
          <div className="font-medium text-gray-900">
            {coverage.carrier.name}
          </div>
        </div>
      )}

      {/* Carrier Address */}
      {coverage.carrier?.address && (
        <div className="mb-4">
          <div className="text-sm text-gray-500 mb-1">
            {isPremises ? 'Insurance Carrier Address' : 'Carrier Address'}
          </div>
          <div className="text-sm text-gray-900">
            {coverage.carrier.address}
          </div>
        </div>
      )}

      {/* Carrier Contact Information */}
      {coverage.carrier?.handles && coverage.carrier.handles.length > 0 && (
        <ContactHandlesList
          handles={coverage.carrier.handles}
          onUpdate={async () => {}} // Read-only
          title=""
          emptyStateTitle="No carrier contacts"
          emptyStateDescription="No contact information available for this carrier."
          readOnly
          inline
          enableCallIntegration
        />
      )}

      {/* Policy identifiers - formatted like a declarations page */}
      {coverage.policy_identifiers &&
        coverage.policy_identifiers.length > 1 && (
          <div className="border-t border-gray-100 pt-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              {coverage.policy_identifiers.map((identifier, idx) => (
                <div key={idx} className="flex justify-between">
                  <span className="text-gray-500">
                    {identifierTypeLabels[identifier.identifier_type]}
                  </span>
                  <span className="font-mono text-gray-900">
                    {identifier.identifier_value}
                  </span>
                  <DataSourceBadge source={identifier.source} />
                </div>
              ))}
            </div>
          </div>
        )}
    </div>
  );
};

const BenefitsAndLimits = ({ coverage }: { coverage: CoverageSchema }) => {
  const [showAllBenefits, setShowAllBenefits] = useState(false);

  if (!coverage.benefits || coverage.benefits.length === 0) {
    return null;
  }

  // Priority benefit types that should always be shown (BI, UM, UIM, PIP)
  const priorityBenefitTypes = [
    'BODILY_INJURY_LIABILITY', // BI
    'UNINSURED_MOTORIST', // UM
    'UNDER_INSURED_MOTORIST', // UIM
    'PERSONAL_INJURY_PROTECTION', // PIP
  ];

  // Group benefits by type for better organization
  const benefitsByType = coverage.benefits.reduce(
    (acc, benefit) => {
      if (!acc[benefit.benefit_type]) {
        acc[benefit.benefit_type] = [];
      }
      acc[benefit.benefit_type].push(benefit);
      return acc;
    },
    {} as Record<string, typeof coverage.benefits>,
  );

  // Separate priority and other benefits
  const priorityBenefits = Object.entries(benefitsByType).filter(
    ([benefitType]) => priorityBenefitTypes.includes(benefitType),
  );
  const otherBenefits = Object.entries(benefitsByType).filter(
    ([benefitType]) => !priorityBenefitTypes.includes(benefitType),
  );

  const hasOtherBenefits = otherBenefits.length > 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <h4 className="font-semibold text-gray-900 mb-4">Coverage Limits</h4>

      <div className="space-y-4">
        {/* Priority benefits - always shown with all limits */}
        {priorityBenefits.map(([benefitType, benefits]) => (
          <div
            key={benefitType}
            className="border-b border-gray-100 last:border-b-0 pb-3 last:pb-0"
          >
            <div className="font-medium text-gray-800 text-sm mb-2 flex items-center gap-2">
              {benefitTypeLabels[benefitType as keyof typeof benefitTypeLabels]}
              {benefits[0]?.source && (
                <DataSourceBadge source={benefits[0].source} />
              )}
            </div>

            {benefits.map((benefit, idx) => (
              <div key={idx} className="ml-3">
                {benefit.limitations &&
                  Object.entries(benefit.limitations).map(([key, value]) => (
                    <div
                      key={key}
                      className="flex justify-between items-center py-1 text-sm"
                    >
                      <span className="text-gray-600 capitalize">
                        {key.toLowerCase()}
                      </span>
                      <span className="font-semibold text-gray-900 tabular-nums">
                        ${formatLimitValue(value)}
                      </span>
                    </div>
                  ))}
              </div>
            ))}
          </div>
        ))}

        {/* Other benefits - shown only when expanded */}
        {showAllBenefits &&
          otherBenefits.map(([benefitType, benefits]) => (
            <div
              key={benefitType}
              className="border-b border-gray-100 last:border-b-0 pb-3 last:pb-0"
            >
              <div className="font-medium text-gray-800 text-sm mb-2 flex items-center gap-2">
                {
                  benefitTypeLabels[
                    benefitType as keyof typeof benefitTypeLabels
                  ]
                }
                {benefits[0]?.source && (
                  <DataSourceBadge source={benefits[0].source} />
                )}
              </div>

              {benefits.map((benefit, idx) => (
                <div key={idx} className="ml-3">
                  {benefit.limitations &&
                    Object.entries(benefit.limitations).map(([key, value]) => (
                      <div
                        key={key}
                        className="flex justify-between items-center py-1 text-sm"
                      >
                        <span className="text-gray-600 capitalize">
                          {key.toLowerCase()}
                        </span>
                        <span className="font-semibold text-gray-900 tabular-nums">
                          ${formatLimitValue(value)}
                        </span>
                      </div>
                    ))}
                </div>
              ))}
            </div>
          ))}

        {/* Progressive disclosure for other benefits */}
        {hasOtherBenefits && (
          <button
            type="button"
            onClick={() => setShowAllBenefits(!showAllBenefits)}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors mt-2"
          >
            {showAllBenefits ? (
              <>
                <ChevronDown className="h-3 w-3" />
                Hide other benefits
              </>
            ) : (
              <>
                <ChevronRight className="h-3 w-3" />
                Show other benefits ({otherBenefits.length})
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

const VehicleAndClaimsInfo = ({ coverage }: { coverage: CoverageSchema }) => {
  const [showPolicyDetails, setShowPolicyDetails] = useState(false);

  // Combine vehicles, claims, and important policy details into one compact section
  const hasVehicles = coverage.vehicles && coverage.vehicles.length > 0;
  const hasClaims = coverage.claims && coverage.claims.length > 0;
  const hasAdjusters = coverage.adjusters && coverage.adjusters.length > 0;
  const hasPolicyDetails =
    coverage.policy_details && coverage.policy_details.length > 0;

  if (!hasVehicles && !hasClaims && !hasAdjusters && !hasPolicyDetails) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <h4 className="font-semibold text-gray-900 mb-4">
        Additional Information
      </h4>

      <div className="space-y-4">
        {/* Vehicles - compact format */}
        {hasVehicles && (
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">
              Vehicles
            </div>
            <div className="space-y-2">
              {(coverage.vehicles || []).map((vehicle, idx) => (
                <div key={idx} className="text-sm bg-gray-50 rounded px-3 py-2">
                  <div className="font-medium text-gray-900">
                    {[vehicle.year, vehicle.make, vehicle.model]
                      .filter(Boolean)
                      .join(' ')}
                    {vehicle.color && ` (${vehicle.color})`}
                  </div>
                  <div className="flex gap-4 text-xs text-gray-600 mt-1">
                    {vehicle.vin && (
                      <span>
                        VIN: <span className="font-mono">{vehicle.vin}</span>
                      </span>
                    )}
                    {vehicle.license_plate && (
                      <span>
                        Plate:{' '}
                        <span className="font-mono">
                          {vehicle.license_plate}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Claims */}
        {(hasClaims || hasAdjusters) && (
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">
              Claims & Adjusters
            </div>
            <div className="space-y-2">
              {hasClaims &&
                (coverage.claims || []).map((claim, idx) => (
                  <div
                    key={idx}
                    className="text-sm bg-amber-50 rounded px-3 py-2"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        {claim.claim_identifier && (
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-gray-900">
                              {claim.claim_identifier}
                            </span>
                            <DataSourceBadge source={claim.source} />
                          </div>
                        )}
                        {claim.benefit_type && (
                          <div className="text-xs text-gray-600">
                            {benefitTypeLabels[claim.benefit_type]}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

              {hasAdjusters &&
                (coverage.adjusters || []).map((adjuster, idx) => (
                  <div
                    key={idx}
                    className="text-sm bg-blue-50 rounded px-3 py-2 space-y-3"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {adjuster.name}
                        </span>
                        <DataSourceBadge source={adjuster.source} />
                      </div>
                      {adjuster.benefit_type && (
                        <div className="text-xs text-gray-600">
                          {benefitTypeLabels[adjuster.benefit_type]} Adjuster
                        </div>
                      )}
                    </div>
                    {adjuster.handles && adjuster.handles.length > 0 && (
                      <ContactHandlesList
                        handles={adjuster.handles}
                        onUpdate={async () => {}} // Read-only
                        title=""
                        emptyStateTitle="No adjuster contacts"
                        emptyStateDescription="No contact information available for this adjuster."
                        readOnly
                        inline
                        enableCallIntegration
                      />
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Progressive disclosure for policy details */}
        {hasPolicyDetails && (
          <div>
            <button
              type="button"
              onClick={() => setShowPolicyDetails(!showPolicyDetails)}
              className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
            >
              {showPolicyDetails ? (
                <>
                  <ChevronDown className="h-3 w-3" />
                  Hide policy details
                </>
              ) : (
                <>
                  <ChevronRight className="h-3 w-3" />
                  Show policy details ({(coverage.policy_details || []).length})
                </>
              )}
            </button>

            {showPolicyDetails && (
              <div className="mt-2 grid grid-cols-1 gap-1 text-xs">
                {(coverage.policy_details || []).map((detail, idx) => (
                  <div
                    key={idx}
                    className="flex justify-between py-1 border-b border-gray-100 last:border-b-0"
                  >
                    <span className="text-gray-600">{detail.key}</span>
                    <span className="text-gray-900 font-medium text-right max-w-xs truncate">
                      {detail.value}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const SubrogationInfo = ({ coverage }: { coverage: CoverageSchema }) => {
  if (!coverage.subrogator) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <h4 className="font-semibold text-gray-900 mb-4">Subrogation</h4>
      <div className="space-y-4">
        <div className="text-sm">
          <div className="font-medium text-gray-900 mb-2">
            {coverage.subrogator.name}
          </div>
        </div>

        {/* Subrogator Contact Information */}
        {coverage.subrogator.handles &&
          coverage.subrogator.handles.length > 0 && (
            <ContactHandlesList
              handles={coverage.subrogator.handles}
              onUpdate={async () => {}} // Read-only
              title=""
              emptyStateTitle="No subrogator contacts"
              emptyStateDescription="No contact information available for this subrogator."
              readOnly
              inline
              enableCallIntegration
            />
          )}

        {/* Subrogator Sources */}
        {coverage.subrogator.sources &&
          coverage.subrogator.sources.length > 0 && (
            <div>
              <div className="text-sm font-medium text-gray-700 mb-2">
                Sources
              </div>
              <div className="space-y-2">
                {coverage.subrogator.sources.map((source, idx) => (
                  <div
                    key={idx}
                    className="text-sm bg-gray-50 rounded px-3 py-2"
                  >
                    <div className="font-medium text-gray-900">
                      {source.title || 'Source'}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        {source.url}
                      </a>
                    </div>
                    {source.description && (
                      <div className="text-xs text-gray-500 mt-1">
                        {source.description}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export const CoverageDetails = ({
  matterId,
  coverage,
  onBack,
  onUpdate,
  backButtonText = 'Back to List',
}: CoverageDetailsProps) => {
  const navigate = useNavigate();
  const [deleteCoverage] = useDeleteCoverageMutation();
  const [updateCoverage] = useUpdateCoverageMutation();
  const [getOrCreatePackage, { isLoading: isCreatingPackage }] =
    useCorrespondencePackageGetOrCreateForCoverageMutation();
  const [isUpdating, setIsUpdating] = useState(false);
  const isV3 = useIsV3();

  // Determine if this coverage supports lien notices (first party health insurance with a carrier)
  const canSendLienNotice =
    coverage.category === CoverageCategory.FIRST_PARTY_HEALTH &&
    !!coverage.carrier_v2_id;

  // Preferred contacts removed; use carrier/division on coverage

  const handleDelete = async () => {
    const proceed = window.confirm(
      'Are you sure you want to delete this coverage?',
    );
    if (proceed) {
      try {
        await deleteCoverage({ matterId, coverageId: coverage.id }).unwrap();
        toast.success('Coverage deleted successfully');
        onBack();
      } catch (error) {
        toast.error('Failed to delete coverage');
      }
    }
  };

  const handleEdit = () => {
    navigate(`/matters/${matterId}/coverage/${coverage.id}/edit`, {
      state: { coverage },
    });
  };

  const handleStatusUpdate = async (newStatus: CoverageStatus) => {
    if (isUpdating) return;

    setIsUpdating(true);
    try {
      await updateCoverage({
        matterId,
        coverageId: coverage.id,
        coverage: {
          status: newStatus,
        },
      }).unwrap();
      onUpdate(); // Refresh the coverage data

      // Show different success messages for cached vs regular coverage
      if (
        newStatus === CoverageStatus.APPROVED &&
        isCachedSuggestion(coverage)
      ) {
        toast.success('Previously verified carrier information accepted');
      } else {
        toast.success(`Coverage ${newStatus.toLowerCase()}`);
      }
    } catch (error) {
      toast.error('Failed to update coverage status');
    } finally {
      setIsUpdating(false);
    }
  };

  const primaryPolicyId = getPrimaryPolicyIdentifier(coverage);

  // Determine status states
  const needsApproval =
    !coverage.status || coverage.status === CoverageStatus.NEEDS_APPROVAL;
  const isApproved = coverage.status === CoverageStatus.APPROVED;
  const isRejected = coverage.status === CoverageStatus.REJECTED;

  // Check if we have evidence to show
  const hasEvidence = coverage.mentions && coverage.mentions.length > 0;

  const carrierDirectoryId = coverage.carrier_v2_id ?? '';
  const { data: directoryCarrier } = useGetCarrierQuery(carrierDirectoryId, {
    skip: !carrierDirectoryId,
  });
  const carrierDisplayName =
    directoryCarrier?.name ?? coverage.carrier?.name ?? 'Unknown Carrier';

  const isPremisesCoverage =
    coverage.category === CoverageCategory.PROPERTY;

  // Fetch property data for premises coverages
  const { data: propertyWithOwners } = usePropertiesWithOwnersQuery(
    {
      id: coverage.property_id ?? '',
      matterId,
    },
    {
      skip: !coverage.property_id || !isPremisesCoverage,
    },
  );

  // For premises, show property name as the main heading (not carrier name)
  const premisesDisplayName = useMemo(() => {
    if (!isPremisesCoverage) return null;
    if (propertyWithOwners?.property_name) {
      return propertyWithOwners.property_name;
    }
    if (propertyWithOwners?.physical_address) {
      const addr = propertyWithOwners.physical_address;
      const parts = [
        addr.line1,
        addr.line2,
        [addr.city, addr.state, addr.zip].filter(Boolean).join(', '),
      ].filter(Boolean);
      return parts.join(', ') || null;
    }
    return null;
  }, [isPremisesCoverage, propertyWithOwners]);

  const pageHeading = isPremisesCoverage
    ? premisesDisplayName || 'Premises Coverage'
    : carrierDisplayName;

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      {(coverage.source === CoverageSource.SHARED || (coverage.source === CoverageSource.OWNED && coverage.shared_to_count > 0)) && (
        <div className="mx-6 mt-4">
          <CoverageSharingBanner coverage={coverage} />
        </div>
      )}
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {backButtonText}
            </Button>
            <Separator orientation="vertical" className="h-6" />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-medium text-gray-900 truncate">
                  {pageHeading}
                </h1>
                {coverage.source === CoverageSource.SHARED && (
                  <Badge
                    variant="outline"
                    className="border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-medium px-2 py-0 h-5 gap-0.5"
                  >
                    <Lock className="h-3 w-3" />
                    Read Only
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1">
                {coverage.policy_type && (
                  <span className="text-sm text-gray-600 truncate max-w-80">
                    {coverage.policy_type}
                  </span>
                )}
                {primaryPolicyId && (
                  <span className="text-sm text-gray-500 font-mono">
                    {primaryPolicyId}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Status and Actions */}
          <div className="flex items-center gap-3">
            {!(isV3 && needsApproval) && (
              <>
                <CoverageStatusBadge status={coverage.status} />

                {needsApproval && coverage.source !== CoverageSource.SHARED && (
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        handleStatusUpdate(CoverageStatus.REJECTED)
                      }
                      disabled={isUpdating}
                      className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                    >
                      <X className="h-4 w-4" />
                      Reject
                    </Button>
                    <Button
                      size="sm"
                      onClick={() =>
                        handleStatusUpdate(CoverageStatus.APPROVED)
                      }
                      disabled={isUpdating}
                      className="gap-2"
                    >
                      <Check className="h-4 w-4" />
                      Approve
                    </Button>
                  </div>
                )}

                {(isApproved || isRejected) && coverage.source !== CoverageSource.SHARED && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      handleStatusUpdate(CoverageStatus.NEEDS_APPROVAL)
                    }
                    disabled={isUpdating}
                    className="text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                  >
                    Reset to Pending
                  </Button>
                )}
              </>
            )}

            {coverage.source !== CoverageSource.SHARED && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleEdit}>
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit Coverage
                  </DropdownMenuItem>
                  {canSendLienNotice && (
                    <DropdownMenuItem
                      disabled={isCreatingPackage}
                      onClick={async () => {
                        try {
                          // Get or create lien notice package with coverage context
                          const result = await getOrCreatePackage({
                            matterId,
                            createCorrespondencePackage: {
                              package_type: 'lien_notice',
                              with_defaults: true,
                              recipient_entity_type: 'insurance_carrier',
                              recipient_entity_id: coverage.carrier_v2_id || '',
                              coverage_id: coverage.id, // Link to coverage for template context
                            },
                          }).unwrap();

                          // Navigate to the package page
                          const returnUrl = `/matters/${matterId}/coverage/${coverage.id}`;
                          navigate(
                            `/matters/${matterId}/correspondence/${result.id}?returnUrl=${encodeURIComponent(returnUrl)}`,
                          );
                        } catch {
                          toast.error('Failed to create lien notice');
                        }
                      }}
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      {isCreatingPackage ? 'Creating...' : 'Send Lien Notice'}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={handleDelete}
                    className="text-red-600 focus:text-red-600"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Coverage
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Only show warning if carrier is linked but has no handles */}
          {(coverage.carrier_v2_id || coverage.carrier?.name) &&
            !(
              coverage.carrier?.handles && coverage.carrier.handles.length > 0
            ) && (
              <div className="mb-3 rounded border border-red-200 bg-red-50 p-2">
                <div className="text-xs font-semibold text-red-800">
                  Missing carrier contact
                </div>
                <div className="text-[11px] text-red-700 mt-1">
                  <p>
                    Attach a carrier contact handle (phone, email, fax, website)
                    to this carrier.
                  </p>
                  <p>
                    Use the Carrier Directory to select the correct division and
                    handles.
                  </p>
                </div>
              </div>
            )}
          {hasEvidence ? (
            /* Two-column layout with evidence */
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              {/* Left Column - Coverage Details */}
              <div className="lg:col-span-2 space-y-4">
                <CoverageOverview
                  coverage={coverage}
                  displayCarrierName={pageHeading}
                  isPremises={isPremisesCoverage}
                />
                <VehicleAndClaimsInfo coverage={coverage} />
                {/* Carrier & Division Selection - only show if carrier is linked */}
                {(coverage.carrier_v2_id || coverage.carrier?.name) && (
                  <div className="bg-white rounded-lg border border-gray-200 p-5">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-900">
                        Carrier Contacts
                      </h4>
                      <CarrierPocketbook
                        trigger={
                          <button
                            type="button"
                            className="flex items-center gap-1.5 px-3 py-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors border border-blue-200 hover:border-blue-300"
                            title="Add Carrier Contact"
                          >
                            <BookOpen className="w-4.5 h-4.5" />
                            <span className="text-sm font-medium">
                              Add Carrier Contact
                            </span>
                          </button>
                        }
                        defaultSelectedCarrierId={
                          coverage.carrier_v2_id || undefined
                        }
                        onSelectDivision={async (carrier, divisionId) => {
                          try {
                            await updateCoverage({
                              matterId,
                              coverageId: coverage.id,
                              coverage: {
                                id: coverage.id,
                                carrier_v2_id: carrier.id,
                                carrier_division_id: divisionId,
                              },
                            }).unwrap();
                            toast.success('Carrier division selected');
                            onUpdate();
                          } catch {
                            toast.error('Failed to update carrier division');
                          }
                        }}
                        actions={['use']}
                      />
                    </div>
                    {coverage.carrier?.handles?.length ? (
                      <ContactHandlesList
                        handles={coverage.carrier.handles}
                        onUpdate={async () => {}}
                        title=""
                        readOnly
                        inline
                        enableCallIntegration
                      />
                    ) : null}
                  </div>
                )}
                <BenefitsAndLimits coverage={coverage} />
                <AutoGeneratedDocuments
                  matterId={matterId}
                  coverageCategory={coverage.category}
                />
                <SubrogationInfo coverage={coverage} />

                {/* Correspondence History */}
                {coverage.carrier_v2_id && (
                  <CorrespondenceHistoryCard
                    matterId={matterId}
                    recipientEntityType="insurance_carrier"
                    recipientEntityId={coverage.carrier_v2_id}
                  />
                )}
              </div>

              {/* Right Column - Evidence */}
              <div className="lg:col-span-3 space-y-6">
                <h2 className="text-lg font-medium text-gray-900">Sources</h2>
                <div className="space-y-6">
                  {coverage.mentions?.map((mention, index) => (
                    <div key={mention.id} className="space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-medium">
                          Source #{index + 1}
                        </span>
                      </div>
                      <EvidenceCard
                        evidence={{
                          artifact_id: mention.artifact_id,
                          citation_ids: mention.citation_ids,
                          context: mention.context,
                          created_at: mention.created_at,
                          artifact_type: mention.artifact_type,
                        }}
                        matterId={matterId}
                        showTimestamp
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* Single column layout without evidence */
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                <CoverageOverview
                  coverage={coverage}
                  displayCarrierName={pageHeading}
                  isPremises={isPremisesCoverage}
                />
                <VehicleAndClaimsInfo coverage={coverage} />
                {/* Carrier & Division Selection - only show if carrier is linked */}
                {(coverage.carrier_v2_id || coverage.carrier?.name) && (
                  <div className="bg-white rounded-lg border border-gray-200 p-5">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-900">
                        Carrier Contacts
                      </h4>
                      <CarrierPocketbook
                        trigger={
                          <button
                            type="button"
                            className="flex items-center gap-1.5 px-3 py-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors border border-blue-200 hover:border-blue-300"
                            title="Add Carrier Contact"
                          >
                            <BookOpen className="w-4.5 h-4.5" />
                            <span className="text-sm font-medium">
                              Add Carrier Contact
                            </span>
                          </button>
                        }
                        defaultSelectedCarrierId={
                          coverage.carrier_v2_id || undefined
                        }
                        onSelectDivision={async (carrier, divisionId) => {
                          try {
                            await updateCoverage({
                              matterId,
                              coverageId: coverage.id,
                              coverage: {
                                id: coverage.id,
                                carrier_v2_id: carrier.id,
                                carrier_division_id: divisionId,
                              },
                            }).unwrap();
                            toast.success('Carrier division selected');
                            onUpdate();
                          } catch {
                            toast.error('Failed to update carrier division');
                          }
                        }}
                        actions={['use']}
                      />
                    </div>
                    {coverage.carrier?.handles?.length ? (
                      <ContactHandlesList
                        handles={coverage.carrier.handles}
                        onUpdate={async () => {}}
                        title=""
                        readOnly
                        inline
                        enableCallIntegration
                      />
                    ) : null}
                  </div>
                )}
                <AutoGeneratedDocuments
                  matterId={matterId}
                  coverageCategory={coverage.category}
                />
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <BenefitsAndLimits coverage={coverage} />
                <SubrogationInfo coverage={coverage} />

                {/* Correspondence History */}
                {coverage.carrier_v2_id && (
                  <CorrespondenceHistoryCard
                    matterId={matterId}
                    recipientEntityType="insurance_carrier"
                    recipientEntityId={coverage.carrier_v2_id}
                  />
                )}
              </div>
            </div>
          )}

          {/* LOR Checker Section */}
        </div>
      </div>
    </div>
  );
};
